import Vue from 'vue';
import find from 'lodash/find';
import filter from 'lodash/filter';
import {loadFiles, loadImages, removeImage, updateFile, updateImage} from '../api/media.api';
import {indexByObjectValue} from '../../../system/helpers/array.helper';

function initialState() {
    return {
        mediaId: null,
        images:  [],
        files:   [],
    }
}

const getters = {

    /**
     * @param {Object} state
     * @return {String}
     */
    mediaId: (state) => state.mediaId,

    /**
     * @param {Object} state
     * @return {Array<Object>}
     */
    images: (state) => state.images,

    /**
     * @param {Object} state
     * @return {Array<Object>}
     */
    files: (state) => state.files,

    /**
     * @param {Object} state
     * @param {Object} getters
     * @return {function(String): Promise<Array<Object>>}
     */
    loadFileById: (state, getters) => async (fileId) => {
        return find(getters.files, (file) => file.id === fileId) || null;
    },

    /**
     * @param {Object} state
     * @param {Object} getters
     * @return {function(Array<String>): Promise<Array<Object>>}
     */
    loadFilesByIds: (state, getters) => async (fileIds) => {
        return filter(getters.files, (file) => fileIds.indexOf(file.id) !== -1);
    },

    /**
     * @param {Object} state
     * @param {Object} getters
     * @return {function(String): Promise<Array<Object>>}
     */
    loadImageById: (state, getters) => async (imageId) => {
        return find(getters.images, (image) => image.id === imageId) || null;
    },

    /**
     * @param {Object} state
     * @param {Object} getters
     * @return {function(String): Promise<Array<Object>>}
     */
    loadImagesByIds: (state, getters) => async (imagesIds) => {
        return filter(getters.images, (image) => imagesIds.indexOf(image.id) !== -1);
    },
}

const actions = {
    /**
     * @param {Function} commit
     * @param {String} mediaId
     * @return {*}
     */
    setMediaId: ({commit}, mediaId) => commit('SET_MEDIA_ID', mediaId),

    /**
     * @param {Function} dispatch
     * @return {Promise<void>}
     */
    async init({dispatch}) {
        await Promise.all([
            dispatch('initFiles'),
            dispatch('initImages'),
        ])
    },

    /**
     * @param {Function} commit
     * @param {Object} getters
     * @return {Promise<void>}
     */
    async initFiles({commit, getters}) {
        const files = await loadFiles(getters.mediaId);
        commit('SET_FILES', files);
    },

    /**
     * @param {Function} commit
     * @param {Object} getters
     * @return {Promise<void>}
     */
    async initImages({commit, getters}) {
        const images = await loadImages(getters.mediaId);
        commit('SET_IMAGES', images);
    },

    /**
     * @param {Function} commit
     * @return {void}
     */
    async updateFile({commit}, data) {
        await updateFile(data);
        commit('UPDATE_FILE', data);
    },

    /**
     * @param {Function} commit
     * @return {void}
     */
    async addFile({commit}, data) {
        commit('ADD_FILE', data);
    },

    /**
     * @param {Function} commit
     * @param {String} fileId
     * @return {void}
     */
    async removeFile({commit}, fileId) {
        await removeImage(fileId);
        commit('REMOVE_FILE', fileId);
    },

    /**
     * @param {Function} commit
     * @param {Object} data
     * @return {void}
     */
    async updateImage({commit}, data) {
        await updateImage(data);
        commit('UPDATE_IMAGE', data);
    },

    /**
     * @param {Function} commit
     * @param {Object} data
     * @return {void}
     */
    async addImage({commit}, data) {
        commit('ADD_IMAGE', data);
    },

    /**
     * @param {Function} commit
     * @param {String} imageId
     * @return {void}
     */
    async removeImage({commit}, imageId) {
        await removeImage(imageId);
        commit('REMOVE_IMAGE', imageId);
    }
}

/**
 * @param {Array<Object>} object
 * @param {String} key
 * @param {String} id
 */
const removeFromArray = (object, key, id) => {
    const index = indexByObjectValue(object[key], 'id', id);
    if (index !== -1) object[key].splice(index, 1)
}

/**
 * @param {Array<Object>} object
 * @param {String} key
 * @param {Object} data
 */
const updateInArray = (object, key, data) => {
    const index = indexByObjectValue(object[key], 'id', data.id);
    if (index) Vue.set(object[key], index, data);
}

const mutations = {
    /**
     * @param {Object} state
     * @param {String} mediaId
     * @return {*}
     * @constructor
     */
    SET_MEDIA_ID: (state, mediaId) => state.mediaId = mediaId,

    /**
     * @param {Object} state
     * @param {Object} data
     * @return {*}
     * @constructor
     */
    SET_FILES: (state, data) => state.files = data,

    /**
     * @param {Object} state
     * @param {Object} data
     * @return {*}
     * @constructor
     */
    SET_IMAGES: (state, data) => state.images = data,

    /**
     * @param {Object} state
     * @param {String} fileId
     * @constructor
     */
    REMOVE_FILE: (state, fileId) => removeFromArray(state, 'files', fileId),

    /**
     * @param {Object} state
     * @param {Object} data
     * @constructor
     */
    UPDATE_FILE: (state, data) => updateInArray(state, 'files', data),

    /**
     * @param {Object} state
     * @param {Object} data
     * @return {*}
     * @constructor
     */
    ADD_FILE: (state, data) => state.files.unshift(data),

    /**
     * @param {Object} state
     * @param {String} imageId
     * @constructor
     */
    REMOVE_IMAGE: (state, imageId) => removeFromArray(state, 'images', imageId),

    /**
     * @param {Object} state
     * @param {Object} data
     * @constructor
     */
    UPDATE_IMAGE: (state, data) => updateInArray(state, 'images', data),

    /**
     * @param {Object} state
     * @param {Object} data
     * @return {*}
     * @constructor
     */
    ADD_IMAGE: (state, data) => state.images.unshift(data),
}

export default {
    namespaced: true,
    state:      initialState,
    getters,
    actions,
    mutations
}
